import { c as _c } from "react/compiler-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { Spinner } from "@/components/old/Spinner";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground hover:bg-primary/90",
      destructive: "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20",
      outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      link: "text-primary underline underline-offset-4"
    },
    size: {
      default: "h-9 px-4 py-2 has-[>svg]:px-3",
      inline: "h-auto leading-none px-2",
      sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
      lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
      xl: "h-12 rounded-md px-6 text-base has-[>svg]:px-4",
      icon: "size-9",
      "icon-sm": "size-8",
      "icon-lg": "size-10"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function Button(t0) {
  const $ = _c(28);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      asChild: t3,
      isSubmitting: t4,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "lg" : t2;
  const asChild = t3 === undefined ? false : t3;
  const isSubmitting = t4 === undefined ? false : t4;
  const Comp = asChild ? Slot : "button";
  let disabled;
  let rest;
  if ($[8] !== props) {
    ({
      disabled,
      ...rest
    } = props);
    $[8] = props;
    $[9] = disabled;
    $[10] = rest;
  } else {
    disabled = $[9];
    rest = $[10];
  }
  const isDisabled = !asChild && (disabled || isSubmitting);
  let t5;
  if ($[11] !== asChild || $[12] !== children || $[13] !== isSubmitting) {
    t5 = isSubmitting && !asChild ? <Spinner /> : children;
    $[11] = asChild;
    $[12] = children;
    $[13] = isSubmitting;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const content = t5;
  let t6;
  if ($[15] !== className || $[16] !== size || $[17] !== variant) {
    t6 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[15] = className;
    $[16] = size;
    $[17] = variant;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = isSubmitting || undefined;
  const t8 = isDisabled || undefined;
  let t9;
  if ($[19] !== Comp || $[20] !== content || $[21] !== rest || $[22] !== size || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== variant) {
    t9 = <Comp data-slot="button" data-variant={variant} data-size={size} className={t6} aria-busy={t7} disabled={t8} {...rest}>{content}</Comp>;
    $[19] = Comp;
    $[20] = content;
    $[21] = rest;
    $[22] = size;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = variant;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}
export { Button, buttonVariants };
export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];